import {
  DCP_REQUEST_START,
  DCP_REQUEST_END,
  DCP_LOADING_STATUS,
  DCP_ACTION_DETECTED,
  DCP_AI_ANSWER,
  DCP_AI_ERROR,
  DCP_STREAM_CHUNK,
  DCP_STREAM_DONE,
  DCP_STREAM_THINKING_CHUNK,
  DCP_MEDIA_BLOCK,
  DCP_MEDIA_STATUS,
  DCP_MEDIA_DONE,
  DCP_RESET,
  DCP_MENTIONS_ADD,
  DCP_MENTION_REMOVE,
  DCP_MENTIONS_CLEAR,
  DCP_MENTIONS_BACKUP,
  DCP_MENTIONS_RESTORE,
  DCP_SET_CHAT_ID,
  DCP_CLEAR_CHAT_ID,
  DCP_CHAT_LIST_SET,
  DCP_CHAT_LIST_APPEND,
  DCP_CHAT_LIST_ADD,
  DCP_CHAT_LIST_UPDATE,
  DCP_CHAT_LIST_REMOVE,
  DCP_PIPELINE_STEP,
  DCP_PIPELINE_STEPS_RESET,
  DCP_HIL_QUESTION,
  DCP_HIL_CLEAR,
  DCP_HIL_REMOVE_QUESTION,
  DCP_BROWSER_TASK_UPDATE,
  DCP_SET_TEMPORARY_MODE,
} from "./CoPilotTypes";

/** A final event for a turn ends it — drop the matching durable turn so its loading spinner stops immediately. */
const endDurableTurn = (state, payload) => {
  const turnId = payload?.turnId ?? payload?.turnID ?? null;
  if (!turnId || !state.durableTurn?.turnId || String(turnId) !== String(state.durableTurn.turnId)) {
    return state.durableTurn;
  }
  return null;
};

/** Chat lists must stay unique by _id — overlapping pagination pages would otherwise produce duplicate React keys. */
const dedupeChats = (chats) => {
  const seen = new Set();
  return (Array.isArray(chats) ? chats : []).filter((chat) => {
    const id = chat?._id != null ? String(chat._id) : "";
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const initialState = {
  loading: false,
  loadingStatus: null,
  action: null,
  answer: null,
  error: null,
  streamingText: null,
  streamingThinkingText: null,
  isStreaming: false,
  streamDonePayload: null,
  mediaBlock: null,
  mediaStatus: null,
  mediaDonePayload: null,
  selectedMentions: [],
  lastStoredMentions: [],
  currentChatId: null,
  durableTurn: null,
  chatList: [],
  chatListTotal: 0,
  pipelineSteps: [],
  hilQuestions: [],
  browserTasks: {},
  temporaryMode: false,
};

export const coPilotReducer = (state = initialState, action) => {
  switch (action.type) {
    case "DCP_DURABLE_TURN": {
      const turn = action.payload;
      const active = ["queued", "running"].includes(turn?.status);
      return { ...state, durableTurn: turn, loading: active && !state.isStreaming,
        loadingStatus: active ? turn.loadingStatus || { title: "Request queued" } : null,
        pipelineSteps: active ? turn.pipelineSteps || [] : [],
        hilQuestions: active ? turn.hilQuestions || [] : [],
        ...(!active ? { isStreaming: false, streamingText: null, streamingThinkingText: null, streamDonePayload: null, answer: null } : {}),
      };
    }
    case DCP_REQUEST_START:
      return {
        ...state,
        loading: true,
        error: null,
        answer: null,
        streamingThinkingText: null,
        pipelineSteps: [],
      };
    case DCP_REQUEST_END:
      return {
        ...state,
        loading: false,
      };
    case DCP_LOADING_STATUS:
      return {
        ...state,
        loadingStatus: action.payload ?? null,
      };
    case DCP_ACTION_DETECTED: {
      const payload = action.payload;
      const actionValue =
        payload != null && typeof payload === "object" && "action" in payload
          ? payload.action
          : payload;
      return {
        ...state,
        action: actionValue ?? null,
      };
    }
    case DCP_AI_ANSWER:
      return {
        ...state,
        loading: false,
        answer: action.payload ?? null,
        error: null,
        loadingStatus: null,
        pipelineSteps: [],
        durableTurn: endDurableTurn(state, action.payload),
      };
    case DCP_AI_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload ?? null,
        answer: null,
        loadingStatus: null,
        pipelineSteps: [],
        durableTurn: endDurableTurn(state, action.payload),
      };
    case DCP_STREAM_CHUNK: {
      const chunk = action.payload ?? "";
      const hasVisibleContent =
        typeof chunk === "string" ? chunk.trim().length > 0 : Boolean(chunk);
      return {
        ...state,
        loading: hasVisibleContent ? false : state.loading,
        loadingStatus: hasVisibleContent ? null : state.loadingStatus,
        isStreaming: state.isStreaming || hasVisibleContent,
        streamingText: (state.streamingText ?? "") + chunk,
      };
    }
    case DCP_STREAM_DONE:
      return {
        ...state,
        isStreaming: false,
        loading: false,
        loadingStatus: null,
        streamDonePayload: action.payload ?? null,
        durableTurn: endDurableTurn(state, action.payload),
      };
    case DCP_STREAM_THINKING_CHUNK:
      return {
        ...state,
        streamingThinkingText: (state.streamingThinkingText ?? "") + (action.payload ?? ""),
      };
    case DCP_MEDIA_BLOCK:
      return {
        ...state,
        mediaBlock: action.payload ?? null,
      };
    case DCP_MEDIA_STATUS:
      return {
        ...state,
        mediaStatus: action.payload ?? null,
      };
    case DCP_MEDIA_DONE:
      return {
        ...state,
        mediaDonePayload: action.payload ?? null,
      };
    case DCP_RESET:
      return {
        ...initialState,
        selectedMentions: state.selectedMentions ?? [],
        lastStoredMentions: state.lastStoredMentions ?? [],
        currentChatId: state.currentChatId,
        chatList: state.chatList ?? [],
        chatListTotal: state.chatListTotal ?? 0,
        temporaryMode: state.temporaryMode ?? false,
        streamingThinkingText: null,
        streamDonePayload: null,
        mediaBlock: null,
        mediaStatus: null,
        mediaDonePayload: null,
        pipelineSteps: [],
      };
    case DCP_SET_TEMPORARY_MODE:
      return { ...state, temporaryMode: action.payload };
    case DCP_SET_CHAT_ID:
      return { ...state, currentChatId: action.payload ?? null };
    case DCP_CLEAR_CHAT_ID:
      return { ...state, currentChatId: null, durableTurn: null, loading: false, loadingStatus: null, pipelineSteps: [], isStreaming: false, streamingText: null };
    case DCP_CHAT_LIST_SET: {
      const p = action.payload;
      const chats = dedupeChats(Array.isArray(p) ? p : (p?.chats ?? []));
      const total = Array.isArray(p) ? p.length : (p?.total ?? chats.length);
      return { ...state, chatList: chats, chatListTotal: total };
    }
    case DCP_CHAT_LIST_APPEND: {
      const chats = action.payload?.chats ?? [];
      const total = action.payload?.total ?? state.chatListTotal ?? 0;
      const list = dedupeChats((state.chatList ?? []).concat(chats));
      return { ...state, chatList: list, chatListTotal: total };
    }
    case DCP_CHAT_LIST_ADD: {
      const chat = action.payload;
      if (!chat || !chat._id) return state;
      const list = state.chatList ?? [];
      if (list.some((c) => c._id === chat._id)) return state;
      const item = {
        _id: chat._id,
        name: chat.name ?? "Chat",
        messageCount: chat.messageCount ?? 2,
        createTime: chat.createTime ?? Date.now(),
        updateTime: chat.updateTime ?? Date.now(),
        categoryIds: chat.categoryIds ?? [],
      };
      return { ...state, chatList: [item, ...list] };
    }
    case DCP_CHAT_LIST_UPDATE: {
      const { chatId, updates } = action.payload ?? {};
      if (!chatId || !updates || typeof updates !== "object") return state;
      const list = state.chatList ?? [];
      const idx = list.findIndex((c) => c._id === chatId);
      if (idx < 0) return state;
      const next = [...list];
      next[idx] = { ...next[idx], ...updates };
      return { ...state, chatList: next };
    }
    case DCP_CHAT_LIST_REMOVE: {
      const chatId = action.payload;
      if (!chatId) return state;
      const list = state.chatList ?? [];
      const next = list.filter((c) => c._id !== chatId);
      if (next.length === list.length) return state;
      const newTotal = Math.max(0, (state.chatListTotal ?? 0) - 1);
      const clearCurrent = state.currentChatId === chatId;
      return {
        ...state,
        chatList: next,
        chatListTotal: newTotal,
        ...(clearCurrent && { currentChatId: null }),
      };
    }
    case DCP_MENTIONS_ADD:
      return {
        ...state,
        selectedMentions: [...(state.selectedMentions ?? []), action.payload],
        lastStoredMentions: [],
      };
    case DCP_MENTION_REMOVE:
      return {
        ...state,
        selectedMentions: (state.selectedMentions ?? []).filter(
          (m) => m.key !== action.payload,
        ),
      };
    case DCP_MENTIONS_CLEAR:
      return { ...state, selectedMentions: [] };
    case DCP_MENTIONS_BACKUP:
      return {
        ...state,
        lastStoredMentions: state.selectedMentions ?? [],
        selectedMentions: [],
      };
    case DCP_MENTIONS_RESTORE: {
      const stored = state.lastStoredMentions ?? [];
      if (stored.length === 0) return state;
      return {
        ...state,
        selectedMentions: [...(state.selectedMentions ?? []), ...stored],
      };
    }
    case DCP_PIPELINE_STEP: {
      const payload = action.payload ?? {};
      const stepId = payload.stepId || `${payload.action || "step"}_${payload.pipelineStep || 0}`;
      const existing = state.pipelineSteps ?? [];
      const idx = existing.findIndex((s) => s.stepId === stepId);

      if (idx >= 0) {
        const next = [...existing];
        next[idx] = { ...next[idx], ...payload, stepId };
        return { ...state, pipelineSteps: next };
      }

      const next = existing.map((s) =>
        s.status === "loading" ? { ...s, status: "completed" } : s
      );
      next.push({ ...payload, stepId, status: payload.stepStatus || "loading" });
      return { ...state, pipelineSteps: next };
    }
    case DCP_PIPELINE_STEPS_RESET:
      return { ...state, pipelineSteps: [] };
    case DCP_HIL_QUESTION: {
      const nextQ = action.payload ?? null;
      if (!nextQ?.questionId) return state;
      const exists = state.hilQuestions.find((q) => q.questionId === nextQ.questionId);
      if (exists) return state;
      return { ...state, hilQuestions: [...state.hilQuestions, nextQ] };
    }
    case DCP_HIL_CLEAR:
      return { ...state, hilQuestions: [] };
    case DCP_BROWSER_TASK_UPDATE: {
      const task = action.payload ?? {};
      const taskId = task.taskId;
      if (!taskId) return state;
      const previous = state.browserTasks?.[taskId] ?? {};
      const frame = task.frame ?? previous.frame;
      if (task.sequence != null && (previous.sequence ?? -1) > task.sequence) return state;
      return {
        ...state,
        browserTasks: {
          ...state.browserTasks,
          [taskId]: { ...previous, ...task, ...(frame ? { frame } : {}) },
        },
      };
    }
    case DCP_HIL_REMOVE_QUESTION: {
      const qid = action.payload;
      return { ...state, hilQuestions: state.hilQuestions.filter((q) => q.questionId !== qid) };
    }
    default:
      return state;
  }
};
